import { Fragment, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const list = (v) => (v && v.length ? v.join(", ") : "—");

// Temporary review workspace: shows only outstanding records of the open bulk batch; hidden once complete
export function BatchReview({ onDone }) {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({});
  const [sel, setSel] = useState({});
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/catalog/governance/bulk-review").then(({ data }) => {
    setRows(data.rows);
    setMeta(data);
    setSel(Object.fromEntries(data.rows.filter((r) => r.governance_status === "DRAFT" && !r.flags.length && !r.publication_errors.length).map((r) => [r.code, true])));
  }).catch(() => setRows([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!rows || !rows.length) return null;
  const chosen = Object.keys(sel).filter((k) => sel[k]);
  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/catalog/governance/bulk-publish", { codes: chosen, note: `${meta.batch} consolidated founder approval` });
      const failed = data.results.filter((r) => !r.published);
      toast.success(`${data.published} color(s) approved & published${failed.length ? ` · ${failed.length} not published` : ""}`);
      failed.forEach((r) => toast.error(`${r.code}: ${r.errors.join("; ")}`));
      setOpen(null); load(); onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const drafts = rows.filter((r) => r.governance_status === "DRAFT");
  return (
    <section className="mt-8 rounded-md border border-border bg-white p-4" data-testid="batch-review">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Batch review · {meta.batch} · {rows.length} outstanding</p>
          <p className="mt-1 text-xs text-warmgrey">{meta.approved} of {meta.total} already approved. Generated drafts — nothing is published until you approve. Flagged records are unticked; tick to include them anyway.</p>
        </div>
        <Button size="sm" className="bg-terracotta text-white hover:bg-terracotta-hover" disabled={busy || !chosen.length} onClick={approve} data-testid="batch-approve-selected">Approve selected ({chosen.length})</Button>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[820px] text-xs" data-testid="batch-review-table">
          <thead><tr className="border-b border-border text-left text-warmgrey">
            <th className="py-2 pr-2"><input type="checkbox" checked={drafts.length > 0 && drafts.every((r) => sel[r.code])} onChange={(e) => setSel(Object.fromEntries(drafts.map((r) => [r.code, e.target.checked])))} data-testid="batch-select-all" /></th>
            {["Code", "Moroccan name", "U.S. display name", "Primary family", "Secondary family", "Status", "Flags", ""].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.code}>
                <tr className="border-b border-border/60" data-testid={`batch-row-${r.code}`}>
                  <td className="py-2 pr-2"><input type="checkbox" disabled={r.governance_status !== "DRAFT"} checked={!!sel[r.code]} onChange={(e) => setSel((x) => ({ ...x, [r.code]: e.target.checked }))} data-testid={`batch-select-${r.code}`} /></td>
                  <td className="py-2 pr-3 font-medium">{r.code}</td>
                  <td className="py-2 pr-3">{r.moroccan_name}{r.name && r.name !== r.moroccan_name ? <span className="text-muted-foreground"> (catalog: {r.name})</span> : null}</td>
                  <td className="py-2 pr-3">{r.approved_us_name}</td>
                  <td className="py-2 pr-3">{r.primary_family || <span className="text-status-reserved">Unresolved</span>}</td>
                  <td className="py-2 pr-3">{r.secondary_family || <span className="text-status-reserved">Unresolved</span>}</td>
                  <td className="py-2 pr-3" data-testid={`batch-status-${r.code}`}>{r.governance_status}</td>
                  <td className="py-2 pr-3 text-status-reserved" data-testid={`batch-flags-${r.code}`}>{r.flags.length ? r.flags.join(" · ") : <span className="text-muted-foreground">—</span>}</td>
                  <td className="py-2"><button className="text-terracotta underline" onClick={() => setOpen(open === r.code ? null : r.code)} data-testid={`batch-inspect-${r.code}`}>{open === r.code ? "Hide" : "Inspect"}</button></td>
                </tr>
                {open === r.code && (
                  <tr className="bg-sand/30" data-testid={`batch-detail-${r.code}`}>
                    <td colSpan={9} className="space-y-1 p-3">
                      <p><span className="font-semibold">Aliases:</span> {list(r.approved_aliases)}</p>
                      <p><span className="font-semibold">Descriptors:</span> {list(r.approved_descriptors)}</p>
                      <p><span className="font-semibold">Rejected:</span> {list(r.rejected_descriptors)}</p>
                      <p><span className="font-semibold">Source / provenance:</span> {r.provenance || "—"}</p>
                      <p><span className="font-semibold">Evidence:</span> {r.evidence || "—"}</p>
                      <p><span className="font-semibold">Internal notes:</span> {r.internal_notes || "—"}</p>
                      <p><span className="font-semibold">SKUs:</span> {list(r.skus)}</p>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
